import { PolicySetting } from '../../../../../types/types';
import { MAP_SETTING_APPEARANCE_TO_CONFIG_FLAG, MAP_UI_CHILD_TO_PARENT } from './constants';
import {
    PASSWORD_PROTECTION,
    PROTECT_ON_DOWNLOAD,
    PREVENT_DOWNLOAD_UNPROTECTED,
    VIEWER_ONLY,
} from '@/consts';

type PolicySettingsDict = Record<string, PolicySetting>;

/**
 * Determine if a policy setting should be shown based on its dependencies
 * and visibility rules (e.g., hiding encryption settings when Block Download is ON)
 */
export const shouldShow = (policySettingsDict: PolicySettingsDict, dependencyId: string, settingId?: string): boolean => {
    // Check visibility rules - hide encryption settings when Block Download is ON
    if (settingId) {
        const viewerOnlySetting = policySettingsDict[VIEWER_ONLY];
        const isViewerOnlyEnabled = viewerOnlySetting && !!viewerOnlySetting.value;
        
        if (isViewerOnlyEnabled) {
            // Hide these settings when Block Download (viewerOnly) is enabled
            const hiddenWhenViewerOnly = [
                PASSWORD_PROTECTION,
                PROTECT_ON_DOWNLOAD,
                PREVENT_DOWNLOAD_UNPROTECTED,
            ];
            
            if (hiddenWhenViewerOnly.includes(settingId)) {
                return false;
            }
        }
        
        // Special case: PREVENT_DOWNLOAD_UNPROTECTED shows when either encryption method is enabled
        if (settingId === PREVENT_DOWNLOAD_UNPROTECTED) {
            const protectOnDownload = policySettingsDict[PROTECT_ON_DOWNLOAD];
            const passwordProtection = policySettingsDict[PASSWORD_PROTECTION];
            const isProtectOnDownloadEnabled = protectOnDownload && !!protectOnDownload.value;
            const isPasswordProtectionEnabled = passwordProtection && !!passwordProtection.value;
            
            return isProtectOnDownloadEnabled || isPasswordProtectionEnabled;
        }
    }
    
    // Check parent dependency (existing logic)
    if (!dependencyId) {
        return true;
    }
    const parent = policySettingsDict[dependencyId];
    return parent ? !!parent.value : true;
};

interface PolicySettingsContent {
    settingsToDisplay: PolicySetting[];
    policySettingsDict: PolicySettingsDict;
}

export const getSettingsContent = (policySettings: PolicySetting[]): PolicySettingsContent => {
    const mapUIParentToChild: Map<string, PolicySetting[]> = new Map<string, PolicySetting[]>();
    const settingsToDisplay: PolicySetting[] = [];
    const policySettingsDict: PolicySettingsDict = {};

    const UITopLevelSettings: PolicySetting[] = [];
    policySettings.forEach((setting) => {
        const showSetting = (
            setting.id !== 'suggestMfa'
            && setting.id !== 'viewerOnly.ToggleWatermark'
            && (typeof MAP_SETTING_APPEARANCE_TO_CONFIG_FLAG[setting.id] === 'boolean'
                ? MAP_SETTING_APPEARANCE_TO_CONFIG_FLAG[setting.id]
                : true)
        );

        if (showSetting) {
            policySettingsDict[setting.id] = setting;
            const UIParentSettingId: string = MAP_UI_CHILD_TO_PARENT[setting.id];
            if (!UIParentSettingId) {
                UITopLevelSettings.push(setting);
            } else {
                mapUIParentToChild.set(
                    UIParentSettingId,
                    [...(mapUIParentToChild.get(UIParentSettingId) || []), setting],
                );
            }
        }
    });

    UITopLevelSettings.forEach((setting) => {
        settingsToDisplay.push(setting);
        if (mapUIParentToChild.has(setting.id)) {
            settingsToDisplay.push(...mapUIParentToChild.get(setting.id));
        }
    });

    return {
        settingsToDisplay,
        policySettingsDict,
    };
};
